import { ERowMovementTypes } from "api/enums";
import { RequiredParamException } from "api/exceptions/InvalidParamException";
import ContractRowModel from "api/models/ContractRowModel";
import ContractRowMovementModel from "api/models/ContractRowMovementModel";
import UserModel from "api/models/UserModel";
import ContractRowMovementRepository from "api/repositories/ContractRowMovementRepository";
import { Transaction } from "firebase/firestore";
import { isEmptyObject } from "utils";

/**
 * Service de movementos das linhas do Contrato
 * @category Contracts
 * @hideconstructor
 */

class ContractRowMovimentService {
  /** Cria um movimento de uma linha aditivada (nova) do contrato. Salva no banco de dados. */
  public async createAdditiveRowMoviment(
    row: ContractRowModel,
    totalContract: number,
    user: UserModel,
    transaction?: Transaction
  ) {
    if (row.isStage) throw new Error("Row can't be a stage!");

    const movement = ContractRowMovementModel.create(row.uid, {
      structured: row.structured,
      contractUid: row.contractUid,
      isStage: row.isStage,
    });
    movement.addAddtiveDataOf(row, row.quantity, totalContract, user);

    this.validateRowMoviment(movement);
    return ContractRowMovementRepository.setMovementRowOfDataBase(
      movement,
      transaction
    );
  }

  /** Altera o movimento de uma linha acrescentando a quantidade aditivada e salva alterações no banco de dados. */
  public async createAdditiveQuantityMoviment(
    row: ContractRowModel,
    addedQuantity: number,
    totalContract: number,
    user: UserModel,
    transaction?: Transaction
  ) {
    if (row.isStage) throw new Error("Row can't be a stage!");

    const movement =
      await ContractRowMovementRepository.getMovementRowFromDataBase(
        row.contractUid,
        row.uid
      );

    if (movement) {
      movement.addAddtiveDataOf(row, addedQuantity, totalContract, user);

      this.validateRowMoviment(movement);
      await ContractRowMovementRepository.setMovementRowOfDataBase(
        movement,
        transaction
      );
    }
  }

  /** Altera o movimento de uma linha acrescentando a quantidade distratada e salva alterações no banco de dados. */
  public async createCancellationMoviment(
    row: ContractRowModel,
    canceledQuantity: number,
    totalContract: number,
    user: UserModel,
    transaction?: Transaction
  ) {
    if (row.isStage) throw new Error("Row can't be a stage!");

    const movement =
      await ContractRowMovementRepository.getMovementRowFromDataBase(
        row.contractUid,
        row.uid
      );

    if (movement) {
      movement.addCancellationDataOf(
        row,
        canceledQuantity,
        totalContract,
        user
      );

      this.validateRowMoviment(movement);
      await ContractRowMovementRepository.setMovementRowOfDataBase(
        movement,
        transaction
      );
    }
  }

  /** Altera o movimento de uma linha acrescentando a quantidade alterada e salva alterações no banco de dados. */
  public async createUpdatedQuantityMoviment(
    row: ContractRowModel,
    updatedQuantity: number,
    totalContract: number,
    user: UserModel,
    transaction?: Transaction
  ) {
    if (row.isStage) throw new Error("Row can't be a stage!");

    const movement =
      await ContractRowMovementRepository.getMovementRowFromDataBase(
        row.contractUid,
        row.uid
      );

    if (movement) {
      movement.addUpdatedDataOf(row, updatedQuantity, totalContract, user);

      this.validateRowMoviment(movement);
      await ContractRowMovementRepository.setMovementRowOfDataBase(
        movement,
        transaction
      );
    }
  }

  public async createDescriptionRowMoviment(
    row: ContractRowModel,
    oldDescription: string,
    user: UserModel,
    transaction?: Transaction
  ) {
    let movement =
      await ContractRowMovementRepository.getMovementRowFromDataBase(
        row.contractUid,
        row.uid
      );

    if (!movement) {
      movement = ContractRowMovementModel.create(row.uid, {
        structured: row.structured,
        contractUid: row.contractUid,
        isStage: row.isStage,
      });
    }

    movement.addDescriptionDataOf(row, oldDescription, user);
    this.validateRowMoviment(movement);

    return ContractRowMovementRepository.setMovementRowOfDataBase(
      movement,
      transaction
    );
  }

  /** Valida se os dados do movimento estão preenchidos. */
  private validateRowMoviment(movement: ContractRowMovementModel) {
    if (!movement.uid) throw new RequiredParamException("uid");
    if (!movement.structured) throw new RequiredParamException("structured");
    if (isEmptyObject(movement.data)) throw new RequiredParamException("data");

    let i = 0;
    for (const mov of Object.values(movement.data)) {
      if (!mov.date) throw new RequiredParamException(`data[${i}].date`);
      if (!mov.type) throw new RequiredParamException(`data[${i}].type`);

      if (mov.type === ERowMovementTypes.EDIT_DESCRIPTION) {
        if (!mov.description)
          throw new RequiredParamException(`data[${i}].description`);
        return;
      }

      if (!mov.quantity && mov.quantity !== 0)
        throw new RequiredParamException(`data[${i}].quantity`);
      if (!mov.unitCost && mov.unitCost !== 0)
        throw new RequiredParamException(`data[${i}].unitCost`);
      if (!mov.total && mov.total !== 0)
        throw new RequiredParamException(`data[${i}].total`);
      if (!mov.totalContract && mov.totalContract !== 0)
        throw new RequiredParamException(`data[${i}].totalContract`);
      i++;
    }
  }

  public async getRowMoviment(
    row: ContractRowModel
  ): Promise<ContractRowMovementModel | undefined> {
    if (!row) return;

    return ContractRowMovementRepository.getMovementRowFromDataBase(
      row.contractUid,
      row.uid
    );
  }

  public async getAllRowsMoviments(
    contractUid: string
  ): Promise<ContractRowMovementModel[]> {
    if (!contractUid) return [];

    return ContractRowMovementRepository.getAllMovementRowsFromDataBase(
      contractUid
    );
  }

  public async updateMovimentAttachments(
    row: ContractRowModel,
    index: number,
    attachmentsUids: string[],
    transaction?: Transaction
  ) {
    if (row.isStage) throw new Error("Row can't be a stage!");

    const details =
      await ContractRowMovementRepository.getMovementRowFromDataBase(
        row.contractUid,
        row.uid
      );

    if (details) {
      const moviments = Object.values(details.data).reverse();
      if (index >= moviments.length) throw new Error("Invalid index");

      moviments[index].attachments = attachmentsUids;
      details.groupAllAttachments();

      await ContractRowMovementRepository.setMovementRowOfDataBase(
        details,
        transaction
      );
    }
  }

  public async hasMoreThanOneMovementWithAttachmentUid(
    contractUid: string,
    attachmentUid: string
  ): Promise<boolean> {
    const docs =
      await ContractRowMovementRepository.getMovementRowFromDataBaseByAttachmentUid(
        contractUid,
        attachmentUid
      );
    return docs.length > 1;
  }
}

export default new ContractRowMovimentService();
